import React from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import MachineColumn from 'components/layout_and_quote_builder/MachineColumn.jsx';

// One card per ManufacturingLine (a machine configuration): for an offset_pair, a printing machine + a
// die-cutting machine; for a flexo_combined, a single combined printing_and_die_cutting_machine. Styled
// like the solution cards. Solutions for the manufacturingLine are searched once it is selected — onSelect.
// bare drops the card chrome (border, shadow, hover, click) so the line can sit INSIDE a Manufacturing Line
// List card as one of its lines, rather than as its own card -- the enclosing list card is the single bordered,
// selectable unit. widthStyle overrides the grid-derived 25% width for that in-list layout. hidePrintingColumn
// / hideDieCuttingColumn drop a machine column that another line in the same list already showed, so a machine
// the parts share (e.g. one die-cutter) is listed once. All are omitted for a standalone card, so that
// rendering is unchanged.
const ManufacturingLineCard = ({ manufacturingLine, onSelect, maxMachineDimensionMm, sectionLabel, widthStyle, bare, hidePrintingColumn, hideDieCuttingColumn }) => {
  const machineColumns = () => {
    if (manufacturingLine.type === 'flexo_combined') {
      return (
        <MachineColumn
          label={`${t('layouts.layout_type_die_cutting')} + ${t('layouts.layout_type_printing')}`}
          machineName={manufacturingLine.printing_and_die_cutting_machine_label}
          machineTypeLabel={manufacturingLine.printing_machine_type_label}
          widthMm={manufacturingLine.printing_sheet_width_mm}
          heightMm={manufacturingLine.printing_sheet_height_mm}
          kind="combined"
          maxMachineDimensionMm={maxMachineDimensionMm}
        />
      );
    }

    // Printing first, then die-cutting — the real production order, matching the solution cards. When a
    // machine already appears on another line of the same Manufacturing Line List (e.g. one die-cutter two
    // parts share), its column is left EMPTY here rather than removed, so the columns stay aligned across the
    // lines and the empty slot signals that machine is the shared one shown above.
    return (
      <>
        {hidePrintingColumn ? <div className={styles.layout_column} /> : (
          <MachineColumn label={t('layouts.layout_type_printing')} machineName={manufacturingLine.printing_machine_label} machineTypeLabel={manufacturingLine.printing_machine_type_label} widthMm={manufacturingLine.printing_sheet_width_mm} heightMm={manufacturingLine.printing_sheet_height_mm} kind="printing" maxMachineDimensionMm={maxMachineDimensionMm} />
        )}
        {hideDieCuttingColumn ? <div className={styles.layout_column} /> : (
          <MachineColumn label={t('layouts.layout_type_die_cutting')} machineName={manufacturingLine.die_cutting_machine_label} machineTypeLabel={manufacturingLine.die_cutting_machine_type_label} widthMm={manufacturingLine.sheet_width_mm} heightMm={manufacturingLine.sheet_height_mm} kind="die_cutting" maxMachineDimensionMm={maxMachineDimensionMm} />
        )}
      </>
    );
  };

  // Which of the section's chosen material sheets (e.g. B3/B2/B1) this ManufacturingLine can run. A
  // line is listed when it runs at least one of the chosen sheets, so without these chips two listed
  // lines would look interchangeable even when one runs every sheet and the other only the smallest.
  const materialDimensionChips = () => {
    const materialLayoutDimensions = manufacturingLine.material_layout_dimensions || [];
    if (materialLayoutDimensions.length === 0) {
      return null;
    }

    return (
      <div style={{ textAlign: 'center', marginTop: '10px' }}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
          {t('quote_builder.material_dimensions')}:
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px' }}>
          {materialLayoutDimensions.map((materialLayoutDimension) => (
            <span
              key={materialLayoutDimension.id}
              style={{
                fontSize: '12px', color: '#555', border: '1px solid #ccc', borderRadius: '4px',
                padding: '2px 8px', backgroundColor: '#f9f9f9'
              }}
            >
              {window.user_unit == 'in' ? materialLayoutDimension.description_in : materialLayoutDimension.description_mm}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const handleClick = () => {
    if (onSelect) {
      onSelect(manufacturingLine);
    }
  };

  // The count needs the section's shared solve, so it is fetched per card AFTER the listing renders
  // (see LayoutNavSectionCombinationList#fetchPricedSolutionCounts). Until it lands, priced_solution_count
  // is undefined and the card shows a small bottom-right spinner instead of a number.
  const hasPricedSolutionCount = typeof manufacturingLine.priced_solution_count === 'number';

  return (
    <div
      className={bare ? undefined : styles.combinations_grid_item_cont}
      data-manufacturing-line-identifier={manufacturingLine.identifier}
      onClick={bare ? undefined : handleClick}
      // Dim the card to 0.6 while its priced_solution_count is still being fetched (the spinner shows in the
      // bottom-right corner meanwhile); it returns to full opacity once the count lands.
      style={{ position: 'relative', opacity: hasPricedSolutionCount ? 1 : 0.6, transition: 'opacity 0.2s ease', ...(widthStyle || {}) }}
    >
      <div className={styles.combinations_grid_item}>
        {/* Which sub_section (Cover / Tray / "Tray & Cover") this line belongs to. The lines are already
            listed under a collapsible sub_section heading, but 35+ cards scroll well past it, so each card
            repeats its section as a badge -- a card read in isolation still shows where it belongs. Reuses
            the same green pill the solution cards use for their part names. Only multi-part sections carry a
            sub_section label; single-part sections pass none and show no badge. */}
        {sectionLabel && (
          <div className={styles.part_badges_container} style={{ justifyContent: 'center', marginBottom: '8px' }}>
            <span className={styles.efficiency_badge}>{sectionLabel}</span>
          </div>
        )}
        <div className={styles.layout_columns_container}>
          {machineColumns()}
        </div>
        {materialDimensionChips()}
        {/* How many of this line's solutions have a unit price (0 unless both machines are priced). */}
        {hasPricedSolutionCount ? (
          <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px' }}>
            {t('quote_builder.priced_solutions')}: <strong>{manufacturingLine.priced_solution_count}</strong>
          </div>
        ) : (
          <div style={{ position: 'absolute', bottom: '8px', right: '8px' }} title={t('quote_builder.priced_solutions')}>
            <span className={styles.cardCountSpinner} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManufacturingLineCard;
